import { useMemo } from "react"
import { useAuthStore, type AuthBusiness } from "@/stores/auth"
import { mergeTenantBusinessSummaries, getDefaultActiveBusinessId } from "@/helpers/console-scope"
import type { AuthSession } from "@/types/auth"

function tenantBusinessesForUser(user: AuthSession | null | undefined): AuthBusiness[] {
  return (mergeTenantBusinessSummaries(user ?? null) ?? []) as AuthBusiness[]
}

/**
 * Hook to get the currently active business.
 * Returns null when multiple businesses exist and none is selected.
 */
export function useActiveBusiness(): AuthBusiness | null {
  const user = useAuthStore((state) => state.user)
  const activeBusinessId = useAuthStore((state) => state.activeBusinessId)
  const getActiveBusiness = useAuthStore((state) => state.getActiveBusiness)

  return useMemo(() => {
    return getActiveBusiness()
  }, [getActiveBusiness, user, activeBusinessId])
}

/**
 * Hook to get all tenant businesses for the current user (owned, admin, provider).
 */
export function useBusinesses(): AuthBusiness[] {
  const user = useAuthStore((state) => state.user)
  return useMemo(
    () => tenantBusinessesForUser(user as AuthSession | null | undefined),
    [user]
  )
}

/**
 * Hook to get active business ID
 */
export function useActiveBusinessId(): string | null {
  const activeBusinessId = useAuthStore((state) => state.activeBusinessId)
  const user = useAuthStore((state) => state.user)
  if (activeBusinessId) return activeBusinessId

  return getDefaultActiveBusinessId(user as AuthSession | null | undefined)
}

/**
 * Hook to set active business
 */
export function useSetActiveBusiness() {
  return useAuthStore((state) => state.setActiveBusiness)
}
